// reduxs/fetchAppointmentsByDoctorId.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { setAppointments } from "../../slices/appointmentsSlice";

// ----------------- PRO LEVEL CACHE ----------------- //
// Cache structure: { [doctorId]: { data: [...], timestamp: number } }
const appointmentsCache = {};
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch appointments by doctorId with memoization, caching, and expiration
 */
export const fetchAppointmentsByDoctorId = createAsyncThunk(
  "appointments/fetchByDoctorId",
  async (doctorId, { dispatch, rejectWithValue }) => {
    if (!doctorId) {
      return rejectWithValue("Doctor ID is required");
    }

    try {
      const now = Date.now();

      // ----------------- USE CACHE IF AVAILABLE ----------------- //
      if (appointmentsCache[doctorId]) {
        const { data, timestamp } = appointmentsCache[doctorId];

        // Use cached data if not expired
        if (now - timestamp < CACHE_EXPIRATION_MS) {
          dispatch(setAppointments(data));
          return data;
        }
      }

      // ----------------- FETCH FROM API ----------------- //
      const baseUrl =
        process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5000";
      const res = await axios.get(`${baseUrl}/appointments/doctor/${doctorId}`);

      // Ensure we always get an array
      const appointmentsArray = Array.isArray(res.data.appointments)
        ? res.data.appointments
        : [];

      // Optional: process testRecommendations or other nested data
      appointmentsArray.forEach((appt) => {
        if (Array.isArray(appt.testRecommendations)) {
          appt.testRecommendations.forEach((test) => {
            // Example: sanitize or transform test data if needed
            test.processed = true; // placeholder
          });
        }
      });

      // ----------------- SAVE TO CACHE ----------------- //
      appointmentsCache[doctorId] = {
        data: appointmentsArray,
        timestamp: now,
      };

      // ----------------- DISPATCH TO REDUX ----------------- //
      dispatch(setAppointments(appointmentsArray));

      return appointmentsArray;
    } catch (err) {
      console.error("❌ Failed to fetch appointments", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);
